import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { useHeaderTakeover } from "../hooks/use-header-takeover";

// Snap rest points. `peek` is a fraction of the viewport height; `full` is a
// fixed pixel line near the top of the screen, so the gap above the sheet
// stays constant across device heights instead of growing on tall screens.
//
// At `full` the sheet has the screen, so it asks the site header to slide away
// (useHeaderTakeover) and takes the room the header had: it rests just under
// the status bar (the safe-area top inset) plus a small gap, not 68px + 18px
// below a header that is no longer there (owner, 2026-10-11: "when a sheet
// opens we need to hide the header"). At `peek` and `collapsed` the map is the
// surface and the header comes back.
//
// The middle snap leaves 48% of the screen as map, so the sheet covers 52% —
// the app's own middle snap. It was 0.62 (a sheet covering 38%), which is
// within 35px of the collapsed snap below on a normal phone and within 1px of
// it on an SE: the two would have been the same rest point wearing two names.
// The SSR fallback transform in discover.css mirrors this number.
const PEEK_RATIO = 0.48;
/** Air between the status bar and the top of a full sheet. */
const FULL_GAP_PX = 10;

/**
 * The floating phone rail, measured off its own CSS: 52px buttons, 6px of
 * frame padding above and below them, and the 12px it floats above the screen
 * edge. The rail's real bottom is `max(12px, safe-area-inset-bottom)`, so the
 * inset is added on top at measure time.
 */
const RAIL_BLOCK_PX = 52 + 12 + 12;
/**
 * Air between the sheet's last visible row at the collapsed snap and that
 * rail. Without it the two touch, and the search field plus four buttons read
 * as one welded control rather than as the sheet and the app's own chrome.
 */
const RAIL_GAP_PX = 14;

/**
 * Three rest points, not two — the same ladder the app has.
 *
 * `collapsed` is the map's snap, and the whole reason for the third one: the
 * sheet's header and whatever row the caller asks to keep (its search field),
 * sitting clear of the floating rail, and nothing else. Until it existed the
 * shallowest snap still covered a third of the phone, so the map this layout
 * is built around was never really on screen. A handle on its own would say
 * the sheet is closed; a search field says it is waiting.
 */
export type SnapId = "collapsed" | "peek" | "full";

/** Lets the surface around the sheet drive its snap — see `handleRef`. */
export interface SheetHandle {
  readonly snapTo: (snap: SnapId) => void;
}

const SNAP_ORDER: readonly SnapId[] = ["full", "peek", "collapsed"];

/**
 * The status-bar inset, refreshed with the collapsed height (on mount and on
 * every resize). Module-level because snapY is called from event handlers
 * that should not each add a probe element to the page.
 */
let safeTopPx = 0;

function snapY(snap: SnapId, h: number, collapsedH: number): number {
  if (snap === "full") return safeTopPx + FULL_GAP_PX;
  const peek = PEEK_RATIO * h;
  if (snap === "peek") return peek;
  // Never higher than peek: on a short viewport (a phone turned sideways) the
  // derived collapsed height can outgrow the peek line, and a "collapsed" sheet
  // resting above "peek" would mean dragging the sheet down made it bigger.
  return Math.max(peek, h - collapsedH);
}

function viewportH(): number {
  if (typeof window === "undefined") return 800;
  return window.visualViewport?.height ?? window.innerHeight;
}

/**
 * The home-indicator gap, in real pixels. Asked of the browser rather than
 * guessed at: it is 0 on most phones and ~34 on an iPhone, and guessing low is
 * what would let the rail ride up over the search field.
 */
function safeAreaPx(edge: "top" | "bottom"): number {
  if (typeof document === "undefined" || !document.body) return 0;
  const probe = document.createElement("div");
  probe.style.cssText = `position:fixed;bottom:0;left:0;width:0;visibility:hidden;pointer-events:none;height:env(safe-area-inset-${edge},0px)`;
  document.body.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  return px;
}

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

interface SheetProps {
  /** Non-scrolling header rendered inside the drag grip (e.g. the title bar). */
  readonly header: ReactNode;
  readonly children: ReactNode;
  /**
   * How much of the scrolling content stays on screen at the collapsed snap.
   * The caller owns this because only the caller knows which of its rows is
   * the one worth keeping — for the browse sheet that is its search field.
   * Left at 0 the collapsed snap shows the header alone.
   */
  readonly collapsedExtraPx?: number;
  /** Optional handle so the map's own controls can ask for a snap. */
  readonly handleRef?: RefObject<SheetHandle | null>;
  /**
   * Where the sheet rests when it first appears. A surface whose map starts
   * hidden (Trips on a phone) opens at `full`, so there is no empty backdrop
   * showing under a sheet that is about to cover it anyway.
   */
  readonly initialSnap?: SnapId;
  /** Told where the sheet came to rest, after a drag or a `snapTo`. */
  readonly onSnapChange?: (snap: SnapId) => void;
}

export function DraggableSheet({
  header,
  children,
  collapsedExtraPx = 0,
  handleRef,
  initialSnap = "peek",
  onSnapChange,
}: SheetProps) {
  const { t } = useTranslation();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const grabRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [snap, setSnap] = useState<SnapId>(initialSnap);
  // Read through a ref so a new callback each render does not re-fire it.
  const onSnapRef = useRef(onSnapChange);
  onSnapRef.current = onSnapChange;
  const firstSnap = useRef(true);
  useEffect(() => {
    if (firstSnap.current) {
      firstSnap.current = false;
      return;
    }
    onSnapRef.current?.(snap);
  }, [snap]);
  const snapRef = useRef<SnapId>(snap);
  // Height of the sheet at its collapsed snap. Measured rather than assumed:
  // the header grows a row when a filter appears, and a hardcoded number would
  // leave the search field half under the rail the moment it did.
  const [collapsedH, setCollapsedH] = useState(0);
  const collapsedHRef = useRef(0);
  // Swallow the click that trails a real drag so a drag doesn't also toggle.
  const suppressClickRef = useRef(false);
  // Timestamp of the last touch, used to ignore the ghost mouse events the
  // browser synthesizes after a tap (prevents double-handling on touch).
  const lastTouchRef = useRef(0);

  useEffect(() => {
    snapRef.current = snap;
  }, [snap]);

  // A full sheet has the screen: the site header steps out of its way.
  useHeaderTakeover(snap === "full");

  // A tap on the header is the "more list / less list" control and deliberately
  // never lands on `collapsed`: that snap belongs to the map, and is reached by
  // dragging or by the map's own button. From collapsed a tap steps back up one
  // rest point rather than jumping the whole way to full.
  const toggleSnap = useCallback(() => {
    setSnap((s) => (s === "full" || s === "collapsed" ? "peek" : "full"));
  }, []);

  // Expose the snap to the surrounding surface (the map's "Open map" button),
  // so it can ask for a rest point by name instead of synthesising a tap on the
  // header and getting whatever the toggle happens to do next.
  useEffect(() => {
    if (!handleRef) return undefined;
    handleRef.current = { snapTo: setSnap };
    return () => {
      handleRef.current = null;
    };
  }, [handleRef]);

  const measureCollapsed = useCallback(() => {
    const grab = grabRef.current;
    if (!grab) return;
    const next =
      grab.offsetHeight +
      collapsedExtraPx +
      RAIL_GAP_PX +
      RAIL_BLOCK_PX +
      safeAreaPx("bottom");
    safeTopPx = safeAreaPx("top");
    collapsedHRef.current = next;
    setCollapsedH(next);
  }, [collapsedExtraPx]);

  useEffect(() => {
    measureCollapsed();
    const grab = grabRef.current;
    if (!grab || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => measureCollapsed());
    observer.observe(grab);
    return () => observer.disconnect();
  }, [measureCollapsed]);

  const applyTransform = useCallback((translateY: number, animate: boolean) => {
    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = animate
      ? "transform 280ms cubic-bezier(.2,.8,.2,1)"
      : "none";
    el.style.transform = `translate3d(0, ${translateY}px, 0)`;
    // Publish the sheet's top edge so the map's floating pin-card can anchor
    // just above it (consumed by .mj-map-floatcard's `bottom` calc).
    document.documentElement.style.setProperty("--mj-sheet-top", `${translateY}px`);
  }, []);

  // Animate to the resting position whenever the snap point changes, and
  // publish the snap state so the floating rail can hide itself at "full"
  // (otherwise the rail overlaps the last cards in the expanded sheet).
  useEffect(() => {
    applyTransform(snapY(snap, viewportH(), collapsedH), true);
    document.documentElement.dataset.mjDiscoverSheet = snap;
    // Collapsed shows the header and one row under it, so a sheet left scrolled
    // halfway down would collapse onto the middle of the list instead of onto
    // the search field the snap exists to show.
    if (snap === "collapsed") scrollRef.current?.scrollTo({ top: 0 });
  }, [snap, collapsedH, applyTransform]);

  // Drop the published sheet-top var on unmount so the (body-portaled) floating
  // pin-card can't anchor to a stale value after the sheet is gone.
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty("--mj-sheet-top");
      delete document.documentElement.dataset.mjDiscoverSheet;
    },
    [],
  );

  // Keep the resting position correct across viewport resizes (mobile URL
  // bar show/hide, orientation change, emulation resize).
  useEffect(() => {
    const onResize = () => {
      measureCollapsed();
      applyTransform(
        snapY(snapRef.current, viewportH(), collapsedHRef.current),
        false,
      );
    };
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [applyTransform, measureCollapsed]);

  // Interaction model, ordered by reliability:
  //
  //   1. TAP / CLICK to toggle peek↔full — the PRIMARY, guaranteed control. A
  //      native `click` listener fires for mouse clicks AND for the synthesized
  //      click the browser emits on a touch tap. The user confirmed ordinary
  //      buttons (clicks) work in their environment even when drag gestures do
  //      not, so this is the dependable path: the whole header toggles the sheet.
  //
  //   2. DRAG via native touch + mouse events — a progressive enhancement. We do
  //      NOT use Pointer Events: under Chromium/Edge device emulation the
  //      Pointer Events + setPointerCapture path claimed the swipe as a pan and
  //      fired pointercancel before any move reached us. Crucially we do NOT
  //      preventDefault on touchstart, so the trailing click still fires for
  //      taps; `touch-action: none` (set in CSS on the grab + descendants)
  //      stops the browser panning, and a non-passive touchmove preventDefault
  //      blocks scroll only once a drag is actually underway.
  //
  // A real drag swallows its trailing click (suppressClickRef) so it doesn't
  // also toggle; ghost mouse events after a touch are ignored via lastTouchRef.
  useEffect(() => {
    const grab = grabRef.current;
    if (!grab) return undefined;

    let drag: { y: number; base: number; moved: number } | null = null;

    const isOptOut = (target: EventTarget | null) =>
      !!(target as Element | null)?.closest("[data-no-drag]");

    // Begin a drag from a given Y, unless the press landed on an opt-out
    // control (the × close button). Returns whether a drag started.
    const begin = (clientY: number, target: EventTarget | null): boolean => {
      if (isOptOut(target)) return false;
      const h = viewportH();
      drag = {
        y: clientY,
        base: snapY(snapRef.current, h, collapsedHRef.current),
        moved: 0,
      };
      return true;
    };

    const move = (clientY: number) => {
      if (!drag) return;
      const dy = clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.abs(dy));
      const h = viewportH();
      const c = collapsedHRef.current;
      applyTransform(
        clamp(drag.base + dy, snapY("full", h, c), snapY("collapsed", h, c)),
        false,
      );
    };

    const end = () => {
      const start = drag;
      drag = null;
      // A near-stationary press is a tap — let the click handler toggle it.
      if (!start || start.moved <= 5) return;

      // A real drag: swallow the trailing click so it doesn't also toggle,
      // then snap to whichever rest point the final position is closest to.
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 350);

      const el = sheetRef.current;
      const h = viewportH();
      const c = collapsedHRef.current;
      let finalTranslate = snapY(snapRef.current, h, c);
      if (el) {
        const match = /translate3d\(\s*0(?:px)?\s*,\s*(-?[\d.]+)px/.exec(
          el.style.transform,
        );
        if (match) finalTranslate = parseFloat(match[1]!);
      }
      // Nearest rest point wins. With three of them a midpoint test no longer
      // says anything — and this deliberately reads only where the finger
      // stopped, never how fast it was going: the native sheet's throw and
      // rubber band are not part of this.
      let nearest: SnapId = snapRef.current;
      let nearestGap = Number.POSITIVE_INFINITY;
      for (const candidate of SNAP_ORDER) {
        const gap = Math.abs(snapY(candidate, h, c) - finalTranslate);
        if (gap < nearestGap) {
          nearestGap = gap;
          nearest = candidate;
        }
      }
      setSnap(nearest);
    };

    // --- Tap / click toggle (primary, reliable) ---
    const onClick = (ev: MouseEvent) => {
      if (isOptOut(ev.target)) return;
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      toggleSnap();
    };

    // --- Touch drag (enhancement) ---
    // touchstart is bound to the grab: that's the only place a drag may start.
    const onTouchStart = (ev: TouchEvent) => {
      lastTouchRef.current = Date.now();
      if (ev.touches.length !== 1) return;
      begin(ev.touches[0]!.clientY, ev.target);
    };
    // touchmove / touchend / touchcancel are bound to the WINDOW (not the
    // grab) so they keep tracking once the finger leaves the grip and travels
    // across the viewport. The backdrop iframe is `pointer-events: none`, so
    // nothing under the finger can intercept the stream. Guarded by `drag` so
    // they no-op unless a drag actually started from the grip.
    const onTouchMove = (ev: TouchEvent) => {
      if (!drag) return;
      ev.preventDefault();
      const tp = ev.touches[0];
      if (tp) move(tp.clientY);
    };
    const onTouchEnd = () => {
      if (!drag) return;
      lastTouchRef.current = Date.now();
      end();
    };

    // --- Mouse drag (desktop) ---
    const onMouseMove = (ev: MouseEvent) => move(ev.clientY);
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      end();
    };
    const onMouseDown = (ev: MouseEvent) => {
      // Ignore the ghost mousedown the browser fires right after a touch.
      if (Date.now() - lastTouchRef.current < 700) return;
      if (!begin(ev.clientY, ev.target)) return;
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    };

    grab.addEventListener("click", onClick);
    grab.addEventListener("touchstart", onTouchStart, { passive: true });
    grab.addEventListener("mousedown", onMouseDown);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      grab.removeEventListener("click", onClick);
      grab.removeEventListener("touchstart", onTouchStart);
      grab.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [applyTransform, toggleSnap]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleSnap();
    }
  };

  return (
    <div ref={sheetRef} className="mj-roamy-sheet">
      <div ref={grabRef} className="mj-roamy-sheet__grab">
        {/*
          The grip is a div with role="button" rather than a native <button>
          on purpose: native buttons intercept touch sequences — the browser's
          tap-cancel behaviour stops delivering touchmove / fires touchcancel
          the moment the press starts to move — which killed any drag that
          began on the grip (while the plain-text title dragged fine). A div
          has no such interception, so the grip now drags like the title; role
          + tabIndex + aria + onKeyDown keep it fully keyboard-accessible.
        */}
        <div
          role="button"
          tabIndex={0}
          aria-label={t("discover.sheet.drag_handle")}
          aria-expanded={snap === "full"}
          onKeyDown={onKeyDown}
          className="mj-roamy-sheet__handle"
        >
          <span aria-hidden="true" className="mj-roamy-sheet__handle-bar" />
        </div>
        {header}
      </div>
      <div ref={scrollRef} className="mj-roamy-sheet__scroll">
        {children}
      </div>
    </div>
  );
}

// SheetTitleBar removed — the mobile sheet title is now the city switcher
// (RoamySheetHeader in discover-mobile-compact.tsx).
